import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/config/app";
import type { PaginatedResponse, PaginationParams } from "@/types/api";
import { buildUrl } from "@/utils/buildUrl";

import type {
  AdminDinerResponse,
  AssignmentsUserRole,
  InvitationResponse,
  IUser,
  IUserBasicInfo,
  IUserDetails,
  IUserInvite,
  NewAssignmentRoleResponse,
} from "./types";

export const usersApi = {
  async postNewUserInvite(user: IUserInvite): Promise<InvitationResponse> {
    const response = await apiClient.post<InvitationResponse>(
      `${API_ENDPOINTS.ADMIN}/partners/invite`,
      user
    );
    return response;
  },

  async getAllUsers(
    params?: PaginationParams
  ): Promise<PaginatedResponse<IUser>> {
    const url = buildUrl(API_ENDPOINTS.ADMIN, "/partners", params);

    const response = await apiClient.get<PaginatedResponse<IUser>>(url);
    return response;
  },

  async getUserById(userId: string): Promise<IUserDetails> {
    const response = await apiClient.get<IUserDetails>(
      `${API_ENDPOINTS.ADMIN}/partners/${userId}`
    );
    return response;
  },

  async patchUser(userId: string, userData: Partial<IUserBasicInfo>) {
    const response = await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/partners/${userId}`,
      userData
    );
    return response;
  },

  async patchAssignmentUserRole(
    assignmentId: string,
    assignData: AssignmentsUserRole
  ) {
    const response = await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/partners/assignments/${assignmentId}`,
      assignData
    );
    return response;
  },

  async unassignmentUserRole(assignmentId: string) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.ADMIN}/partners/assignments/${assignmentId}`
    );
    return response;
  },

  async postNewAssignmentRole(
    id: string,
    data: AssignmentsUserRole
  ): Promise<NewAssignmentRoleResponse> {
    const response = await apiClient.post<NewAssignmentRoleResponse>(
      `${API_ENDPOINTS.ADMIN}/partners/${id}/assignments`,
      data
    );
    return response;
  },

  async getUserAssignedRoleById(
    userId: string,
    params?: PaginationParams
  ): Promise<AdminDinerResponse> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/partners/${userId}/assignments`,
      params
    );

    const response = await apiClient.get<AdminDinerResponse>(url);
    return response;
  },

  async postResendInvitation(id: string): Promise<IUserDetails> {
    const response = await apiClient.post<IUserDetails>(
      `${API_ENDPOINTS.ADMIN}/partners/${id}/resend-invitation`
    );
    return response;
  },

  async postReactivateUser(id: string): Promise<IUserDetails> {
    const response = await apiClient.post<IUserDetails>(
      `${API_ENDPOINTS.ADMIN}/partners/${id}/reactivate`
    );
    return response;
  },

  async getPartnerScopeUsers(
    params?: PaginationParams
  ): Promise<PaginatedResponse<IUser>> {
    const url = buildUrl(API_ENDPOINTS.ADMIN, "/partners/scope-users", params);

    const response = await apiClient.get<PaginatedResponse<IUser>>(url);
    return response;
  },
};
